/**
 * V112-3-10 (ADR-0099 §4–§5, owner D3 / D5): Settings → Profile → Data & Scope, the workspace defaults.
 *
 * Each answer shows how many systems set their own value (a drilldown to each one, with its value and who set it),
 * or that every system follows it. Industries with several values list the systems that need a sector (D3); a
 * system the server held on its old sector reads "Waiting on sector: … (kept)" (D5).
 *
 * Saving previews first: Review changes runs the dry run (POST /profile/scope-preview, any member) and shows, per
 * system, the rules the change adds or removes. Each reached system can be kept on its current answer: after the
 * profile save, that system's classification is PATCHed with its current value, so it becomes its own (owner /
 * admin, as the PATCH enforces). Saving is owner / admin / editor (PUT /api/profiles/:id); others read only.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox } from '@aigovops/design-system';
import { effectiveScope, overridesOf, type ParsedCustomerProfile, type ProfileInput, type ScopeAnswer, type ScopeOverrideMeta, type ScopePreview } from '@aigovops/core';
import { listWorkspaceSystems, previewWorkspaceScope, updateProfile, updateSystemClassification, type WsSystemRow } from '../../lib/api';
import { queryKeys, useInvalidate } from '../../lib/query-keys';
import { useWorkspaceQuery } from '../../hooks/useWorkspaceQuery';
import { Caret, ScopeAnswerRow, ScopeChip, StampChip } from './ScopeAnswerRow';
import { RowEditor } from './ScopeEditors';
import { ScopeSaveBar } from './ScopeSaveBar';
import { ScopeSummaryStrip, focusAnswer, type StripCell } from './ScopeSummaryStrip';
import {
  REMOVE_NOTE, ROW_ANSWERS, ROW_HINT, ROW_KEYS, ROW_NAME, SYSTEM_ROW_NAME, changeWords, classificationOnlyText, classificationRows, depRows, depText,
  fmt, inheritedSector, isKnownIndustry, parseJson, plural, profileDefaults, profilePatch, profileRows, rowBody, rowMeta, sameSet, stampWho, valueItems,
  type Classification, type RowKey,
} from './scope-options';
import './scope.css';

interface SysInfo {
  id: string; name: string; own: ScopeAnswer[]; meta: ScopeOverrideMeta | null; rows: Record<RowKey, string[]>; C: Classification;
}

const ownRow = (s: SysInfo, r: RowKey) => ROW_ANSWERS[r].some((a) => s.own.includes(a));
const ownName = (r: RowKey) => (r === 'sector' ? 'sector' : r === 'federal' ? 'US federal role' : ROW_NAME[r].toLowerCase());
const rowsOfAnswers = (answers: readonly ScopeAnswer[]) => ROW_KEYS.filter((r) => ROW_ANSWERS[r].some((a) => answers.includes(a)));

function sysInfo(s: WsSystemRow, profile: ParsedCustomerProfile): SysInfo {
  const C = parseJson<Classification>(s.classification_json, {});
  const raw = parseJson<unknown>(s.scope_overrides_json ?? null, null);
  const stored = Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : null;
  const overrides = stored ?? (s.classification_json ? null : []);
  const meta = parseJson<ScopeOverrideMeta | null>(s.scope_override_meta_json ?? null, null);
  return {
    id: s.system_id, name: s.name, own: overridesOf(overrides, C), meta, C,
    rows: classificationRows(effectiveScope(C, profileDefaults(profile), overrides)),
  };
}

export function WorkspaceScope({ slug, profile, canEdit, canKeep, onSaved }: {
  slug: string;
  profile: ParsedCustomerProfile;
  /** Owner / admin / editor: may change the defaults. */
  canEdit: boolean;
  /** Owner / admin: may keep a system on its current answer (a classification save). */
  canKeep: boolean;
  onSaved: (profile: ParsedCustomerProfile) => void;
}) {
  const invalidate = useInvalidate();
  const sys = useWorkspaceQuery((s) => queryKeys.scopeSystems(s), (s) => listWorkspaceSystems(s));
  const systems = useMemo(() => (sys.data?.systems ?? []).map((s) => sysInfo(s, profile)), [sys.data, profile]);
  const saved = useMemo(() => profileRows(profile), [profile]);
  const [edits, setEdits] = useState<Partial<Record<RowKey, string[]>>>({});
  const draft = (r: RowKey) => edits[r] ?? saved[r];
  const dirty = (r: RowKey) => r in edits && !sameSet(edits[r]!, saved[r]);
  const dirtyRows = ROW_KEYS.filter(dirty);
  const [sheet, setSheet] = useState<{ open: boolean; loading: boolean; preview: ScopePreview | null; error: string | null; failed?: 'preview' | 'save' }>({ open: false, loading: false, preview: null, error: null });
  const [keeps, setKeeps] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<ReactNode>(null);
  const [announcement, setAnnouncement] = useState<{ text: string; n: number } | null>(null);
  const say = (text: string) => setAnnouncement((a) => ({ text, n: (a?.n ?? 0) + 1 }));
  const sysHref = (id: string) => `/w/${slug}/app/systems/${encodeURIComponent(id)}?tab=scope`;

  const needsSector = (s: SysInfo, industries: readonly string[]) =>
    industries.length > 1 && (!ownRow(s, 'sector') || !!s.meta?.sector?.waiting);

  // ---- Strip: one cell per system ----
  const cells: StripCell[] = systems.map((s) => {
    const own = ROW_KEYS.filter((r) => ownRow(s, r) && !(r === 'sector' && s.meta?.sector?.waiting));
    const need = needsSector(s, saved.sector);
    return {
      key: s.id, to: sysHref(s.id),
      top: <span className="sc-sysname">{s.name}<Caret /></span>,
      chips: <>
        {need ? <ScopeChip kind="todo">Needs a sector</ScopeChip> : null}
        {own.length ? <ScopeChip kind="set">{`Sets ${own.length} of ${ROW_KEYS.length}`}</ScopeChip> : need ? null : <ScopeChip kind="inherit">Follows every default</ScopeChip>}
      </>,
      names: own.length ? own.map((r) => SYSTEM_ROW_NAME[r]).join(', ') : need ? 'Otherwise follows defaults' : undefined,
    };
  });

  // ---- Per-answer indicator ----
  function indicator(r: RowKey): ReactNode {
    if (!systems.length) return null;
    const industries = draft('sector');
    if (r === 'sector' && industries.length > 1) {
      const live = saved.sector.length > 1;
      const need = systems.filter((s) => needsSector(s, industries));
      const keptFor = (s: SysInfo) => fmt('sector', s.rows.sector.length ? s.rows.sector : saved.sector);
      return (
        <details className="sc-disc" data-todo={need.length > 0}>
          <summary><Caret />{need.length ? `${plural(need.length, 'system', 'systems')} ${live ? (need.length === 1 ? 'needs' : 'need') : 'will need'} a sector` : 'Each system picks its own sector'}</summary>
          <ul className="sc-disc-body">
            {systems.map((s) => needsSector(s, industries) ? (
              <li key={s.id}>
                <span style={{ fontWeight: 600 }}>{s.name}</span>
                {live ? (
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', alignItems: 'center' }}>
                    <ScopeChip kind="todo">{s.meta?.sector?.waiting ? `Waiting on sector: ${keptFor(s)} (kept)` : 'Needs a sector'}</ScopeChip>
                    <Link to={sysHref(s.id)}>Pick sector<span className="sr-only">{` for ${s.name}`}</span></Link>
                  </span>
                ) : <span className="sc-meta">{`Keeps ${keptFor(s)}, waiting on sector`}</span>}
              </li>
            ) : (
              <li key={s.id}><Link to={sysHref(s.id)}>{s.name}</Link><span>{fmt('sector', s.rows.sector)}</span><span className="sc-meta">{`Set for this system · ${stampWho(rowMeta(s.meta, 'sector'))}`}</span></li>
            ))}
            {!live && need.length ? <li className="sc-meta">Pick sectors after you confirm</li> : null}
          </ul>
        </details>
      );
    }
    const setters = systems.filter((s) => ownRow(s, r));
    if (!setters.length) return <p className="sc-follows"><ScopeChip kind="check">{`All ${plural(systems.length, 'system follows', 'systems follow')} this`}</ScopeChip></p>;
    return (
      <details className="sc-disc">
        <summary><Caret />{setters.length === 1 ? '1 system sets its own' : `${setters.length} systems set their own`}</summary>
        <ul className="sc-disc-body">{setters.map((s) => (
          <li key={s.id}><Link to={sysHref(s.id)}>{s.name}</Link><span>{fmt(r, s.rows[r])}</span><span className="sc-meta">{`Set for this system · ${stampWho(rowMeta(s.meta, r))}`}</span></li>
        ))}</ul>
      </details>
    );
  }

  /** Who last changed a saved default: "Updated · Nadia O., 12 Oct" (reason on press); unstamped = "Set before Oct 2026".
   *  "Changed" stays for unsaved edits only. */
  const defaultMeta = (profile.scope_default_meta ?? null) as ScopeOverrideMeta | null;
  const stamp = (r: RowKey): ReactNode => {
    const m = rowMeta(defaultMeta, r);
    if (m) return <StampChip state="Updated" meta={m} kind="quiet" id={`st-${r}-when`} />;
    return saved[r].length ? <ScopeChip kind="quiet" parts={['Set before Oct 2026', '']} /> : null;
  };

  // ---- Preview ----
  async function review() {
    if (!dirtyRows.length) return;
    setKeeps(new Set());
    setSheet({ open: true, loading: true, preview: null, error: null });
    try {
      const change = profilePatch(Object.fromEntries(dirtyRows.map((r) => [r, draft(r)])));
      const preview = await previewWorkspaceScope(slug, change as Parameters<typeof previewWorkspaceScope>[1]);
      setSheet({ open: true, loading: false, preview, error: null });
    } catch {
      setSheet({ open: true, loading: false, preview: null, error: 'We couldn’t check what this changes, so it can’t be saved yet.', failed: 'preview' });
    }
  }
  const closeSheet = (focusRow?: RowKey) => {
    setSheet((s) => ({ ...s, open: false }));
    if (focusRow) window.setTimeout(() => focusAnswer(focusRow), 0);
  };
  const p = sheet.preview;
  const byId = new Map(systems.map((s) => [s.id, s]));
  /** Rows a system can be kept on: the changed rows it inherits; never for a system that needs a sector (D5 keeps its
   *  sector server-side). */
  const keepable = (s: ScopePreview['systems'][number]) => {
    const info = byId.get(s.system_id);
    if (s.needs_answer.length || !info) return [];
    // A keep pins the system to the one value it has now. A sector needs exactly one effective (known) sector:
    // none, or a system that needs a sector, gets no keep.
    return rowsOfAnswers(s.inherits).filter((r) => r !== 'sector'
      || (info.rows.sector.length === 1 && isKnownIndustry(info.rows.sector[0]!) && !needsSector(info, draft('sector'))));
  };
  const fullyKept = (s: ScopePreview['systems'][number]) => {
    const rows = rowsOfAnswers(s.inherits);
    return rows.length > 0 && rows.every((r) => keeps.has(`${s.system_id}:${r}`));
  };
  const reached = (p?.systems ?? []).filter((s) => s.inherits.length)
    .sort((a, b) => (b.added.length + b.removed.length) - (a.added.length + a.removed.length));
  const notReached = (p?.systems ?? []).filter((s) => !s.inherits.length && s.sets_own.length);
  const counted = reached.filter((s) => !fullyKept(s));
  const A = counted.reduce((n, s) => n + s.added.length, 0), R = counted.reduce((n, s) => n + s.removed.length, 0);
  const dA = new Set(counted.flatMap((s) => s.added.map((x) => x.rule_id))).size;
  const dR = new Set(counted.flatMap((s) => s.removed.map((x) => x.rule_id))).size;
  const changedSystems = counted.filter((s) => s.added.length || s.removed.length).length;
  const need = reached.filter((s) => s.needs_answer.length);
  const title = !p ? 'Couldn’t check what this changes' : !A && !R ? 'No obligation changes'
    : `This ${[A ? `adds ${plural(dA, 'rule', 'rules')}` : '', R ? `removes ${dR}${A ? '' : dR === 1 ? ' rule' : ' rules'}` : ''].filter(Boolean).join(' and ')} on ${plural(changedSystems, 'system', 'systems')}`;
  const sheetHead = p ? (
    <>
      {A || R ? <p className="sc-count">{`System obligations: ${[A ? `${A} added` : '', R ? `${R} removed` : ''].filter(Boolean).join(', ')}`}</p> : null}
      <ul className="sc-changes">{dirtyRows.map((r) => (
        <li key={r}><button type="button" className="sc-link" onClick={() => closeSheet(r)}><b>{ROW_NAME[r]}</b></button> {changeWords(r, saved[r], draft(r))}</li>
      ))}</ul>
      {need.length ? (
        <p className="sc-need"><ScopeChip kind="todo">{`${plural(need.length, 'system needs', 'systems need')} a sector`}</ScopeChip>
          <span>{need.map((s) => s.name).join(', ')}</span><span style={{ color: 'var(--text-muted)' }}>· Obligations stay, waiting on sector</span></p>
      ) : null}
    </>
  ) : null;
  /** Every answer the rule depends on, with this system's values (after the change for an added rule, before it for a
   *  removed one). The changed answers it inherits carry "↩" and lead back to them. */
  const because = (s: ScopePreview['systems'][number], x: ScopePreview['systems'][number]['added'][number], add: boolean) => {
    const info = byId.get(s.system_id);
    const reach = rowsOfAnswers(s.inherits).filter(dirty);
    const before = info?.rows ?? saved;
    const after = { ...before } as Record<RowKey, string[]>;
    for (const r of reach) after[r] = r === 'sector' ? (inheritedSector(draft('sector')).length ? inheritedSector(draft('sector')) : before.sector) : draft(r);
    const deps = depRows(x.depends_on);
    const keys = deps.length ? deps : reach;
    return (
      <span className="sc-because">
        {keys.filter((k) => depText(k, add ? after : before, () => '', x.jurisdiction) !== null).map((k, i) => {
          const text = depText(k, add ? after : before, (o) => (info ? classificationOnlyText(info.C, o) : null), x.jurisdiction);
          const changed = (reach as string[]).includes(k);
          return (
            <span key={k}>{i ? <span aria-hidden="true">{' + '}<span className="sr-only"> and </span></span> : null}
              {changed ? (
                <button type="button" className="sc-link" data-changed onClick={() => closeSheet(k as RowKey)}>
                  <span aria-hidden="true">↩ </span>{text}<span className="sr-only"> (back to this answer)</span>
                </button>
              ) : <span>{text}</span>}
            </span>
          );
        })}
      </span>
    );
  };
  const ruleLi = (s: ScopePreview['systems'][number], x: ScopePreview['systems'][number]['added'][number], add: boolean) => (
    <li key={`${add ? 'a' : 'r'}-${x.rule_id}`}>
      <span className="sc-k" data-k={add ? 'add' : 'rem'}>{add ? '+ Adds' : '− Removes'}</span>
      <span style={{ display: 'grid', gap: 2 }}>
        <span className="sc-rule-name">{x.statute}{x.citation ? <span className="sc-cite">{` · ${x.citation}`}</span> : null}</span>
        {!add ? <span>{REMOVE_NOTE}</span> : null}
        {because(s, x, add)}
      </span>
    </li>
  );
  const sheetBody = p ? (
    <>
      {reached.map((s, i) => {
        const kept = rowsOfAnswers(s.inherits).filter((r) => keeps.has(`${s.system_id}:${r}`));
        const full = fullyKept(s);
        const showRules = !full && (s.added.length || s.removed.length);
        return (
          <details key={s.system_id} className="sc-sys" open={reached.length <= 3 || i === 0 || undefined}>
            <summary><Caret /><span>{s.name}</span>
              {s.needs_answer.length ? <ScopeChip kind="todo">Needs a sector</ScopeChip> : null}
              <span className="sc-delta">{showRules ? <>
                {s.added.length ? <span className="sc-d" data-k="add">{`+${s.added.length}`}<span className="sr-only"> added</span></span> : null}
                {s.removed.length ? <span className="sc-d" data-k="rem">{`−${s.removed.length}`}<span className="sr-only"> removed</span></span> : null}
              </> : <span className="sc-d" data-k="none">No change</span>}</span>
            </summary>
            {kept.length ? <p className="sc-kept">{`Kept for this system on Confirm: ${kept.map(ownName).join(', ')}`}</p> : null}
            {s.needs_answer.length ? <p className="sc-kept">{`Keeps ${fmt('sector', saved.sector)}, waiting on sector`}</p> : null}
            {!s.needs_answer.length && !kept.length && !s.added.length && !s.removed.length
              ? <p className="sc-kept">{`Takes the new ${rowsOfAnswers(s.inherits).map(ownName).join(', ')}; no obligation change`}</p> : null}
            {showRules ? <ul className="sc-rules">{s.added.map((x) => ruleLi(s, x, true))}{s.removed.map((x) => ruleLi(s, x, false))}</ul> : null}
            {keepable(s).length ? (
              <div className="sc-keep">
                {keepable(s).map((r) => {
                  const k = `${s.system_id}:${r}`;
                  return (
                    <Checkbox key={k} id={`keep-${s.system_id}-${r}`} checked={keeps.has(k)} disabled={!canKeep}
                      aria-describedby={canKeep ? undefined : `keep-who-${s.system_id}`}
                      label={r === 'sector' ? `Keep ${s.name} on ${fmt('sector', byId.get(s.system_id)!.rows.sector)}` : `Keep ${s.name} on its current ${ownName(r)}`}
                      onChange={(e) => { const on = e.currentTarget.checked; setKeeps((cur) => { const n = new Set(cur); if (on) n.add(k); else n.delete(k); return n; }); }} />
                  );
                })}
                {!canKeep ? <p className="sc-kept" id={`keep-who-${s.system_id}`}>An owner or admin can keep a system on its current answer.</p> : null}
              </div>
            ) : null}
          </details>
        );
      })}
      {notReached.length ? (
        <p className="sc-unaffected">Not reached: {notReached.map((s, i) => (
          <span key={s.system_id}>{i ? '; ' : ''}<Link to={sysHref(s.system_id)}>{s.name}</Link>{` (sets its own ${rowsOfAnswers(s.sets_own).map(ownName).join(', ')})`}</span>
        ))}</p>
      ) : null}
    </>
  ) : null;

  async function confirm() {
    if (busy || !p) return;
    setBusy(true);
    // The value each kept system has now (before the defaults change), written as its own answer.
    const keepWrites = [...keeps].map((k) => {
      const [id, r] = k.split(':') as [string, RowKey];
      const s = byId.get(id);
      return s ? { s, r, body: rowBody(r, s.rows[r]) } : null;
    }).filter((x): x is { s: SysInfo; r: RowKey; body: Record<string, unknown> } => !!x && !!x.body);
    // Keeps FIRST (each system's current answer becomes its own), then the defaults. A failed keep stops here, before
    // anything about the defaults changes (keeps already written only pin a system to the value it has today).
    for (const w of keepWrites) {
      try {
        await updateSystemClassification(slug, w.s.id, w.body as Parameters<typeof updateSystemClassification>[2]);
      } catch (e) {
        const status = (e as { status?: number } | null)?.status;
        setSheet((st) => ({ ...st, error: `${w.s.name} couldn’t be kept on its current ${ownName(w.r)}${status === 403 ? ' (an owner or admin can do this)' : ''}. The workspace defaults haven’t changed.` }));
        invalidate('classification:changed', slug);
        setBusy(false);
        return;
      }
    }
    try {
      const patch = profilePatch(Object.fromEntries(dirtyRows.map((r) => [r, draft(r)])));
      const res = await updateProfile(profile.profile_id, { ...patch, status: 'active', ...(reason.trim() ? { scope_reason: reason.trim() } : {}) } as Partial<ProfileInput> & { scope_reason?: string });
      const names = counted.filter((s) => s.added.length || s.removed.length).map((s) => s.name);
      setDone(
        <div className="sc-done" tabIndex={-1} id="sc-done">
          <ScopeChip kind="check">Saved</ScopeChip>
          <span>{names.length ? `Obligations updated on ${names.join(' and ')}.` : 'No obligations changed.'}</span>
          {need.map((s) => <Link key={s.system_id} to={sysHref(s.system_id)}>{`Pick a sector for ${s.name}`}</Link>)}
          {!need.length ? <Link to={`/w/${slug}/app/systems`}>View systems</Link> : null}
        </div>,
      );
      setEdits({}); setKeeps(new Set()); setReason('');
      setSheet({ open: false, loading: false, preview: null, error: null });
      say('Saved.');
      invalidate('profile:changed', slug);
      invalidate('classification:changed', slug);
      onSaved(res.profile);
      window.setTimeout(() => document.getElementById('sc-done')?.focus(), 0);
    } catch (e) {
      const status = (e as { status?: number } | null)?.status;
      setSheet((s) => ({ ...s, failed: 'save', error: status === 401 ? 'Your session has expired. Sign in again, then try again.'
        : status === 403 ? 'Your role in this workspace doesn’t allow changing the workspace defaults. An owner, admin or editor can.'
          : 'That didn’t save. The workspace defaults haven’t changed.' }));
    } finally {
      setBusy(false);
    }
  }
  const discard = () => {
    const first = dirtyRows[0];
    setEdits({});
    say('Changes discarded.');
    if (first) window.setTimeout(() => focusAnswer(first), 0);
  };

  return (
    <div className="sc-root">
      <section className="sc-section" aria-labelledby="sc-ws-h">
        <div className="sc-section-head"><h3 id="sc-ws-h">Workspace defaults</h3></div>
        {cells.length ? <ScopeSummaryStrip label="Systems and the defaults they follow" cells={cells} /> : null}
      </section>
      {done}
      <div className="sc-card">
        {ROW_KEYS.map((r) => {
          const val = draft(r);
          const hint = r === 'sector' ? (val.length > 1 ? 'Each system picks its own sector' : undefined) : ROW_HINT[r];
          return (
            <ScopeAnswerRow key={r} answer={r} name={ROW_NAME[r]} hint={hint} state="plain" dirty={dirty(r)} headingLevel="h4"
              headExtra={<>{dirty(r) ? null : stamp(r)}{indicator(r)}</>}>
              {canEdit ? (
                <RowEditor row={r} stem={r} value={val} saved={saved[r]} describedBy={hint ? `d-${r}` : undefined}
                  onChange={(next, cleared) => {
                    setDone(null);
                    setEdits((m) => ({ ...m, [r]: next }));
                    if (cleared?.length) say(`Cleared ${cleared.map((c) => valueItems(r, [c])[0]).join(', ')}.`);
                  }} />
              ) : <ul className="sc-value" role="list">{valueItems(r, val).length ? valueItems(r, val).map((x) => <li key={x}>{x}</li>) : <li>Not answered</li>}</ul>}
            </ScopeAnswerRow>
          );
        })}
      </div>
      {canEdit ? (
        <ScopeSaveBar
          names={dirtyRows.map((r) => ROW_NAME[r])}
          open={sheet.open}
          loading={sheet.loading}
          busy={busy}
          title={title}
          head={sheetHead}
          ready={!!p}
          error={sheet.error}
          onRetry={() => void (sheet.failed === 'save' ? confirm() : review())}
          retryLabel={sheet.failed === 'save' ? 'Try again' : 'Check again'}
          resultText={p ? (typeof title === 'string' ? title : undefined) : undefined}
          reasonWhere={`workspace ${dirtyRows.map((r) => ROW_NAME[r]).join(', ')}`}
          reason={reason}
          onReason={setReason}
          onReview={() => void review()}
          onDiscard={discard}
          onBack={() => closeSheet()}
          onConfirm={() => void confirm()}
          announcement={announcement}
        >
          {sheetBody}
        </ScopeSaveBar>
      ) : null}
    </div>
  );
}
